// DynamicRenderer.tsx (Corrected)
import React, { useMemo } from 'react';
import { transform } from 'sucrase';
import * as Lucide from 'lucide-react';
import * as Primitives from './primitives';

// The type for the function we will dynamically create.
type SlideComponentFunction = (props: any) => React.ReactElement;

/**
 * Creates a renderable React component function from a string of code.
 */
const createRenderFn = (componentCode: string): SlideComponentFunction => {
  try {
    const fullCode = `return (${componentCode});`;
    const jsCode = transform(fullCode, {
      transforms: ['jsx'],
      jsxPragma: 'React.createElement',
      jsxFragmentPragma: 'React.Fragment',
    }).code;
    
    const scope = { React, ...Primitives, ...Lucide };
    const factory = new Function(...Object.keys(scope), jsCode);
    
    return factory(...Object.values(scope));

  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : String(error);
    return (props: any) => ( // The error component can accept props to avoid further errors
      <div style={{ backgroundColor: '#ef4444', color: 'white', padding: '2rem', fontFamily: 'monospace' }}>
        <h3>Error Compiling Slide</h3>
        <pre style={{ whiteSpace: 'pre-wrap', marginTop: '1rem' }}>{errorMessage}</pre>
        <h4 style={{ marginTop: '1rem', borderTop: '1px solid white', paddingTop: '1rem' }}>Offending Code:</h4>
        <pre style={{ whiteSpace: 'pre-wrap', opacity: 0.8 }}>{props.code || componentCode}</pre>
      </div>
    );
  }
};

interface DynamicRendererProps {
  code: string;
  // It will receive all other props and pass them through to the dynamic component.
  [key: string]: any;
}

export const DynamicRenderer: React.FC<DynamicRendererProps> = ({ code, ...props }) => {
  const SlideComponent = useMemo(() => createRenderFn(code), [code]);

  // THE FIX: Pass all the other props (like `data` and `theme`) down to the dynamic component.
  return <SlideComponent {...props} />;
};

// Define a default scope that can be used by previews and engines.
export const defaultRendererScope = {
  React,
  ...Lucide,
  ...Primitives,
};